// src/IT/pages/ITRequestDetail.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getITRequest,
  itMe,
  addITComment,
  uploadITAttachment,
  decideITRequest,
} from "../../api/ITapi";
import StatusPill from "../components/StatusPill";
import AttachmentList from "../components/AttachmentList";
import ActivityTable from "../components/ActivityTable";

export default function ITRequestDetail() {
  const { id } = useParams();
  const requestId = Number(id);

  const [me, setMe] = useState(null);
  const [req, setReq] = useState(null);
  const [comment, setComment] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [meData, reqData] = await Promise.all([itMe(), getITRequest(requestId)]);
    setMe(meData);
    setReq(reqData);
  };

  useEffect(() => {
    load().catch((e) => alert(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  const canApprove = useMemo(() => {
    if (!me || !req) return false;
    const perms = (me.permissions || []).map((p) => String(p).toUpperCase());
    const has = perms.includes("IT_REQUEST_APPROVE");
    return has && req.status === "PENDING_MANAGER" && me.id === req.manager_user_id;
  }, [me, req]);

  const doComment = async () => {
    if (!comment.trim()) return;
    setBusy(true);
    try {
      await addITComment(requestId, comment.trim());
      setComment("");
      await load();
    } catch (e) {
      alert(e.message);
    } finally {
      setBusy(false);
    }
  };

  const doUpload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      await uploadITAttachment(requestId, file);
      setFile(null);
      await load();
    } catch (e) {
      alert(e.message);
    } finally {
      setBusy(false);
    }
  };

  const doDecision = async (decision) => {
    setBusy(true);
    try {
      await decideITRequest(requestId, decision, comment.trim() || null);
      setComment("");
      await load();
    } catch (e) {
      alert(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!req) return <div className="it-page">Loading…</div>;

  return (
    <div className="it-page">
      <div className="it-card">
        <div className="it-row">
          <div>
            <div className="it-card-title">IT Request #{req.id}</div>
            <div className="it-muted">
              {req.request_type} • <StatusPill status={req.status} />
            </div>
          </div>
        </div>

        <div className="it-grid2" style={{ marginTop: 8 }}>
          <div className="it-kv">
            <div className="it-label">Title</div>
            <div><b>{req.title}</b></div>
          </div>
          <div className="it-kv">
            <div className="it-label">Requester</div>
            <div>{req.requester_email}</div>
          </div>
          <div className="it-kv">
            <div className="it-label">Manager</div>
            <div>{req.manager_email || "-"}</div>
          </div>
          <div className="it-kv">
            <div className="it-label">Created</div>
            <div>{new Date(req.created_at).toLocaleString()}</div>
          </div>
        </div>

        <div style={{ marginTop: 10 }}>
          <div className="it-label">Description</div>
          <div style={{ whiteSpace: "pre-wrap" }}>{req.description}</div>
        </div>

        <div style={{ marginTop: 10 }}>
          <div className="it-label">Details</div>
          <pre className="it-pre">{JSON.stringify(req.details || {}, null, 2)}</pre>
        </div>

        <div className="it-grid2" style={{ marginTop: 10 }}>
          <label className="it-field">
            <div className="it-label">Add Comment</div>
            <input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Notes / reason..." />
          </label>

          <label className="it-field">
            <div className="it-label">Upload Attachment</div>
            <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
        </div>

        <div className="it-actions">
          <button className="it-btn-sm" disabled={busy || !comment.trim()} onClick={doComment}>
            Add Comment
          </button>

          <button className="it-btn-sm" disabled={busy || !file} onClick={doUpload}>
            Upload
          </button>

          <button className="it-btn-sm" disabled={busy || !canApprove} onClick={() => doDecision("approve")}>
            Approve
          </button>

          <button className="it-btn-sm it-danger" disabled={busy || !canApprove} onClick={() => doDecision("reject")}>
            Reject
          </button>
        </div>
      </div>

      <div style={{ marginTop: 12 }}>
        <AttachmentList attachments={req.attachments} />
      </div>

      <div style={{ marginTop: 12 }}>
        <ActivityTable events={req.events} />
      </div>
    </div>
  );
}
